import { lazy, useEffect, useMemo, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { NVButton } from '@nv/basic-components'
import { ListTable, useNVModal } from '@nv/components'
import { useConfigurationContext } from '@nv/contexts'
import { usePageNumber, usePersistedFiltersMultiSelect } from '@nv/hooks'
import { AdminVoterService } from '@nv/services'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './AdminEligibleList.module.scss'
const SuccessDownloaded = lazy(() => import('@nv/lazy-loadable/lib/modals/SuccessDownloaded'))
const Downloading = lazy(() => import('@nv/lazy-loadable/lib/modals/Downloading'))
const UserFilters = lazy(() => import('@nv/lazy-loadable/lib/modals/UserFilters'))
const BarcodeScanner = lazy(() => import('@nv/lazy-loadable/lib/modals/BarcodeScanner'))

const NUM_VOTERS_PER_PAGE = 50

export const AdminEligibleList = ({ selectedColumns, searchableStrings }: any) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [numUsers, setNumUsers] = useState(0)
  const [searchInput, setSearchInput] = useState()
  const { page, handlePageChange } = usePageNumber()
  const { NVModal: SuccessDownloadedModal, openModal: openSuccessDownloadedModal } = useNVModal()
  const { NVModal: DownloadingModal, openModal: openDownloadingModal, closeModal: closeDownloadingModal } = useNVModal()
  const { NVModal: UserFiltersModal, openModal: openUserFiltersModal } = useNVModal()
  const { NVModal: BarcodeScannerModal, openModal: openBarcodeScannerModal } = useNVModal()
  const { eligibleListfilterOptions: filterOptions } = useConfigurationContext()

  const { filters, handleFilterChange }: any = usePersistedFiltersMultiSelect({
    options: filterOptions,
    onFilterChange: () => handlePageChange(0),
  })

  const getStatus = (info) => {
    if (info.removed) return 'removed'
    else if (info.voted) return 'voted'
    else if (info.registered) return 'registered'
    return 'registered'
  }

  const columns = useMemo(() => {
    return [
      {
        id: 'ElectorId',
        accessorKey: 'electorId',
        cell: (info) => info.getValue() || '',
        header: () => t('admin.electorsList.voterId'),
      },
      {
        id: 'KnownIdNumber',
        accessorKey: 'knownIdNumber',
        cell: (info) => info.getValue() || '',
        header: () => t('admin.electorsList.knownIdNumber'),
      },
      {
        id: 'FirstName',
        accessorKey: 'firstName',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.firstName'),
      },
      {
        id: 'MiddleName',
        accessorKey: 'middleName',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.middleName'),
      },
      {
        id: 'LastName',
        accessorKey: 'lastName',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.lastName'),
      },
      {
        id: 'DateOfBirth',
        accessorKey: 'dateOfBirth',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.dateOfBirth'),
      },
      {
        id: 'Address',
        accessorKey: 'address',
        cell: (info) => {
          const address = info.getValue()
          return address.fullAddress
        },
        header: () => t('form.labels.address'),
      },
      {
        id: 'Action',
        accessorKey: 'action',
        cell: (info) => (
          <NVButton
            text={t('main.details')}
            onClick={() => {
              const voterId = info.row.original?.id
              navigate(`/admin/elector-details/${voterId}?pageState=${btoa(window.location.pathname + window.location.search)}`)
            }}
          />
        ),
        header: () => <span>{t('buttons.action')}</span>,
      },
      {
        id: 'District',
        accessorKey: 'district',
        cell: (info) => info.getValue() || '',
        header: () => t('admin.electorsList.district'),
      },
      {
        id: 'VotingChannel',
        accessorKey: 'votingChannel',
        cell: (info) =>
          !info.getValue() ? (
            ''
          ) : (
            <span className={clsx(styles[`votingChannel-label`], info.getValue() ? styles[`votingChannel-label__${info.getValue()}`] : '')}>
              {t(`admin.electorsList.votedChannelOptions.${info.getValue()}`)}
            </span>
          ),
        header: () => t('admin.electorsList.votingChannel'),
      },
      {
        id: 'Status',
        accessorKey: 'registered',
        cell: (info) =>
          !getStatus(info.row.original) ? (
            ''
          ) : (
            <span className={clsx(styles[`status-label`], styles[`status-label__${getStatus(info.row.original)}`])}>
              {t(`admin.electorsList.eligibleVoterStatusOptions.${getStatus(info.row.original)}`)}
            </span>
          ),
        header: () => t('admin.electorsList.eligibleVoterStatus'),
      },
    ].filter((column) => selectedColumns?.includes(column.id))
  }, [currentLanguage])

  const getUsers = async () => {
    setLoading(true)
    const arrToStrFilters = (arr) => arr?.map((a) => a.value).join(',')

    const resp = await AdminVoterService.fetchEligibleVoters(
      filters.districtFilter
        ? filterOptions.find((f) => f.key === 'districtFilter')?.isMulti
          ? arrToStrFilters(filters.districtFilter)
          : filters.districtFilter.value
        : 'all',
      filters.channelFilter
        ? filterOptions.find((f) => f.key === 'channelFilter')?.isMulti
          ? arrToStrFilters(filters.channelFilter)
          : filters.channelFilter.value
        : 'all',
      NUM_VOTERS_PER_PAGE,
      page * NUM_VOTERS_PER_PAGE,
      searchInput,
      filters.statusFilter
        ? filterOptions.find((f) => f.key === 'statusFilter')?.isMulti
          ? arrToStrFilters(filters.statusFilter)
          : filters.statusFilter.value
        : 'all',
      filters.voteByMailFilter
        ? filterOptions.find((f) => f.key === 'voteByMailFilter')?.isMulti
          ? arrToStrFilters(filters.voteByMailFilter)
          : filters.voteByMailFilter.value
        : 'all',
      filters.voteByMailAddressFilter
        ? filterOptions.find((f) => f.key === 'voteByMailAddressFilter')?.isMulti
          ? arrToStrFilters(filters.voteByMailAddressFilter)
          : filters.voteByMailAddressFilter.value
        : 'all',
    )
    if (resp?.users) {
      setUsers(resp.users)
      setNumUsers(resp.numUsers)
    }
    setLoading(false)
  }
  useEffect(() => {
    getUsers()
  }, [page, filters, searchInput])

  useEffect(() => {
    if (filters.votingChannel?.value !== 'inPerson' && filters.voteByMailFilter?.length) {
      setTimeout(() => {
        handleFilterChange('channelFilter', { value: 'mail', label: t('admin.electorsList.votedChannelOptions.mail') })
      }, 300)
    }
  }, [filters.voteByMailFilter])

  useEffect(() => {
    if (
      filters.votingChannel?.value !== 'inPerson' &&
      filters.voteByMailAddressFilter?.value &&
      filters.voteByMailAddressFilter?.value !== 'all'
    ) {
      setTimeout(() => {
        handleFilterChange('channelFilter', { value: 'mail', label: t('admin.electorsList.votedChannelOptions.mail') })
      }, 300)
    }
  }, [filters.voteByMailAddressFilter])

  const getDowloadUrl = async () => {
    openDownloadingModal()
    const arrToStrFilters = (arr) => arr?.map((a) => a.value).join(',')

    const res = await AdminVoterService.fetchEligibleVotersCsv(
      filters.districtFilter
        ? filterOptions.find((f) => f.key === 'districtFilter')?.isMulti
          ? arrToStrFilters(filters.districtFilter)
          : filters.districtFilter.value
        : 'all',
      filters.channelFilter
        ? filterOptions.find((f) => f.key === 'channelFilter')?.isMulti
          ? arrToStrFilters(filters.channelFilter)
          : filters.channelFilter.value
        : 'all',
    )
    if (res?.success) {
      // @ts-ignore
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.download = `voterlist`
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      closeDownloadingModal()
      openSuccessDownloadedModal()
    }
  }

  const getVBMPackageBulk = async (ids) => {
    openDownloadingModal()
    const res = await AdminVoterService.getVoteByMailPackageVoterBulk(ids)
    if (res?.success) {
      // @ts-ignore
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.download = `vbm-${new Date()}`
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      closeDownloadingModal()
      openSuccessDownloadedModal()
    }
  }

  const refreshList = () => getUsers()

  const electorMultiRowActions = []
  if (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) {
    electorMultiRowActions.push({
      label: t('admin.electorsList.printVbm'),
      value: 'printVBM',
      callback: (selectedIds) => {
        getVBMPackageBulk(selectedIds)
      },
    })
  }

  return (
    <>
      <BarcodeScannerModal>
        <BarcodeScanner />
      </BarcodeScannerModal>

      <DownloadingModal>
        <Downloading />
      </DownloadingModal>

      <SuccessDownloadedModal>
        <SuccessDownloaded />
      </SuccessDownloadedModal>

      <UserFiltersModal>
        <UserFilters filterOptions={filterOptions} appliedFilters={filters} onFilterChange={handleFilterChange} />
      </UserFiltersModal>

      <div className={styles['container']}>
        <ListTable
          data={users}
          columns={columns}
          currentPageNum={page}
          dataLength={numUsers}
          download={KeycloakAdminService.isSuperAdmin() && getDowloadUrl}
          onPageChange={handlePageChange}
          loading={loading}
          showFilter={KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()}
          onSearch={(input) => {
            setSearchInput(input)
            handlePageChange(0)
          }}
          searchValue={searchInput}
          setFilterModal={openUserFiltersModal}
          searchPlaceholder='admin.electorsList.search'
          paginationLabel='admin.electorsList.electors'
          refreshList={refreshList}
          numVotersPerPage={NUM_VOTERS_PER_PAGE}
          noDataLabel={t('admin.electorsList.noDataEligible')}
          onTriggerSearch={setSearchInput}
          searchableStrings={searchableStrings}
          numRowsPerPage={NUM_VOTERS_PER_PAGE}
          multiRowActions={electorMultiRowActions}
        />
      </div>
    </>
  )
}
